import { Col, Modal, Row } from "react-bootstrap";
import { useUserStore } from "../../../../../zustand/userStore";
import { FavoriteCard } from "./FavoriteCard";
import { useState, useEffect } from "react";
import ProductModal from "../../products/ProductModal";
import { createAuthenticatedAxiosInstance, toggleFavorite, getFavorites, fetchPublicProductById } from "api/auth";
import { useAuthStore } from "../../../../../zustand/loginStore";
import CustomToast from "components/general/CustomToast";
import LoadingScreen from "components/general/LoadingScreen";
import { PublicProductDetails, IFavoriteProduct } from "interfaces/IProduct";
import { useBreakpoints } from "hooks/useBreakPoints";

/**
 * Main component for the list of favorites of the user's profile
 * @returns 
 */
export const Favorites = () => {
    const { token } = useAuthStore();
    const { isMobile } = useBreakpoints();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<{ message: string, show: boolean, success: boolean }>({ message: "", show: false, success: true });
    const [productId, setProductId] = useState<number | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [favoriteProductsDetails, setFavoriteProductsDetails] = useState<IFavoriteProduct[]>([]);
    const { favoriteProducts, setFavoriteProducts, setFavoritesLoaded } = useUserStore();
    const axiosInstance = createAuthenticatedAxiosInstance(token);
    


    /**
     * Convert PublicProductDetails to IFavoriteProduct
     */
    const convertToFavoriteProduct = (productDetails: PublicProductDetails): IFavoriteProduct => {
        // Verificar si el producto tiene colores (está disponible)
        const isAvailable = productDetails.colors && productDetails.colors.length > 0;

        // Si el producto no tiene colores, retornar con estado false
        if (!isAvailable) {
            return {
                product_id: productDetails.id,
                product_name: productDetails.name,
                description: '',
                price: 0,
                brand: productDetails.brand,
                images: [],
                sizes: [],
                status: false
            };
        }

        // Get all unique sizes from all colors
        const allSizes = productDetails.colors.flatMap(color => 
            color.sizes.map(size => size.name)
        );
        const uniqueSizes = Array.from(new Set(allSizes));

        // Get all images from all colors
        const allImages = productDetails.colors.flatMap(color => color.images);

        // Get the minimum price from all sizes
        const allPrices = productDetails.colors.flatMap(color => 
            color.sizes.map(size => size.price)
        );
        const minPrice = Math.min(...allPrices);

        return {
            product_id: productDetails.id,
            product_name: productDetails.name,
            description: productDetails.colors[0]?.description || '',
            price: minPrice,
            brand: productDetails.brand,
            images: allImages,
            sizes: uniqueSizes,
            status: true
        };
    };

    /**
     * Load favorite details when favoriteProducts change
     */
    useEffect(() => {
        const loadFavoriteDetails = async () => {
            if (favoriteProducts.length > 0) {
                setIsLoading(true);
                try {
                    // Get unique product IDs to avoid duplicates
                    const uniqueProductIds = Array.from(
                        new Set(favoriteProducts.map(fav => fav.product_id))
                    );

                    // Obtener detalles completos de cada producto favorito único
                    const favoriteDetailsPromises = uniqueProductIds.map(async (productId) => {
                        try {
                            const productDetails = await fetchPublicProductById(productId);
                            return convertToFavoriteProduct(productDetails);
                        } catch (error) {
                            console.error(`Error loading product ${productId}:`, error);
                            return null;
                        }
                    });

                    const favoriteDetails = await Promise.all(favoriteDetailsPromises);
                    const validFavoriteDetails = favoriteDetails.filter((detail): detail is IFavoriteProduct => detail !== null);
                    setFavoriteProductsDetails(validFavoriteDetails);
                } catch (error) {
                    console.error("Error loading favorite details:", error);
                    setShowToast({ 
                        message: "Error al cargar los detalles de los productos favoritos", 
                        show: true, 
                        success: false 
                    });
                } finally {
                    setIsLoading(false);
                }
            } else {
                setFavoriteProductsDetails([]);
                setIsLoading(false);
            }
        };

        loadFavoriteDetails();
    }, [favoriteProducts]); // Depend on favoriteProducts from the store

    /**
     * Function that triggers the modal
     */
    const handleShowModal = (productId: number) => {
        setShowModal(true);
        setProductId(productId);
    }

    /**
     * Function that removes the product from the wishlist
     */
    const handleRemove = async (productId: number) => {
        if (token) {
            const { data, success } = await toggleFavorite(axiosInstance, productId);
            if (success && data) {
                setFavoriteProducts(data);
                setFavoritesLoaded(true); // Ensure it's marked as loaded
                // También remover de los detalles locales
                setFavoriteProductsDetails(prev => 
                    prev.filter(product => product.product_id !== productId)
                );
                setShowToast({ 
                    message: "El producto fue removido exitosamente de la lista de favoritos.", 
                    show: true, 
                    success: true 
                });
            } else {
                setShowToast({ 
                    message: "No se pudo remover el producto de la lista de favoritos, por favor intente nuevamente.", 
                    show: true, 
                    success: false 
                });
            }
        }
    }

    // Mostrar loading mientras se cargan los favoritos
    if (isLoading) {
        return <LoadingScreen loadingText="Cargando productos favoritos..." />;
    }

    return (
        <div className="favorites-container">
            {favoriteProductsDetails?.length > 0 ? (
                <Row className={`justify-content-start mx-1 mx-md-4 ${isMobile ? 'mobile-favorites-row' : ''}`}>
                    {favoriteProductsDetails.map((product, index) => (
                        <Col 
                            key={`Favorite card ${index}`} 
                            className={`mb-3 px-1 px-md-3 ${isMobile ? 'mobile-favorite-col' : ''}`} 
                            xs={6} 
                            sm={6} 
                            md={4} 
                            lg={4}
                        >
                            <FavoriteCard
                                handleRemove={handleRemove}
                                showModal={handleShowModal}
                                product={product}
                                isMobile={isMobile}
                            />
                        </Col>
                    ))}
                </Row>
            ) : (
                <div className={`text-center py-5 ${isMobile ? 'mobile-no-favorites-full-width' : ''}`}>
                    <h4 className={isMobile ? 'mobile-no-favorites-title' : ''}>No tienes productos favoritos</h4>
                    <p className={`text-muted ${isMobile ? 'mobile-no-favorites-text' : ''}`}>
                        Explora nuestro catálogo y agrega productos a tu lista de favoritos 
                        haciendo clic en el corazón ♥
                    </p>
                </div>
            )}
            {productId &&
                <ProductModal
                    productId={productId}
                    show={showModal}
                    onHide={() => setShowModal(false)}
                />
            }
            <CustomToast 
                message={showToast.message}
                onClose={() => {setShowToast({message: "", show: false, success: true})}}
                show={showToast.show}
                success={showToast.success}
                width="250px"
            />
        </div>
    )
}
